// ============================================================
// SecaoCadastroAtletas.js — o cadastro geral dos atletas
//
// A lista que a coordenação mantinha num documento de texto à parte, com o
// retrato de uniforme colado ao lado de nome, gênero, CPF, nascimento e
// modalidade. A cada prestação de contas ela era redigitada, e foi assim
// que a mesma pessoa saiu com uma data de nascimento num arquivo e outra
// no sistema.
//
// Aqui ela é a leitura do cadastro, não uma segunda digitação: todos os
// campos vêm de `users`, preenchidos em Gerenciar usuários, e a única coisa
// que esta tela grava é a foto.
//
// ── Por que a foto é desta tela e não daquela ───────────────
//
// Porque o atleta precisa existir para ter foto. No formulário de criação
// ainda não há documento onde guardar o caminho do arquivo, e um envio que
// acontece antes do cadastro é um arquivo órfão esperando para dar errado.
//
// É o RETRATO DE UNIFORME, e nunca a foto do reconhecimento facial. As duas
// vivem em pastas diferentes no servidor, com funções diferentes: usar o
// retrato como referência biométrica trocaria o consentimento de uma
// finalidade pelo de outra.
//
// ── O desligado continua na lista ───────────────────────────
//
// Em preto e branco, que é o que diz "esta pessoa esteve no programa" sem
// precisar de uma linha de texto. Sumir com ele apagaria da vista o atleta
// cujas competições e avaliações continuam contando no histórico do ciclo.
// ============================================================

import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  carregarMembrosGestao, obterFotoDeCadastro,
  salvarFotoDeCadastro, removerFotoDeCadastro,
} from "../firebase";
import { reduzirImagem, lerArquivoComoDataUrl, partesDoDataUrl } from "../imagem";
import { idadeEm } from "../idade";
import { TEXTO, CINZA_ESC, BRANCO, BORDA, FUNDO_SUAVE, RAIO, ESP, TIPO, PERIGO } from "../tema";
import {
  Card, Btn, Sel, StatusBox, Carregando, Vazio, Etiqueta,
  Estatistica, FileiraDeEstatisticas, CampoDeBusca, Avatar,
} from "../componentes";
import { useNomeNucleo } from "../NucleosContexto";
import useIsMobile from "../useIsMobile";

function emBR(iso) {
  if (!iso) return "";
  const p = String(iso).split("-");
  return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : String(iso);
}

const ROTULO_DO_GENERO = { masculino: "Masculino", feminino: "Feminino" };

// "1 desligados" é o tipo de coisa que faz a tela parecer rascunho.
function plural(n, singular) {
  return `${n} ${singular}${n === 1 ? "" : "s"}`;
}

export default function SecaoCadastroAtletas({ userNucleo }) {
  const nucleo = userNucleo || "";
  const nomeDoNucleo = useNomeNucleo();
  const isMobile = useIsMobile();

  const [carregando, setCarregando] = useState(true);
  const [status, setStatus] = useState(null);
  const [atletas, setAtletas] = useState([]);
  const [fotos, setFotos] = useState({});       // uid -> data URL
  const [ocupado, setOcupado] = useState(null); // uid com envio em curso
  const [busca, setBusca] = useState("");
  const [modalidade, setModalidade] = useState("");
  const [situacao, setSituacao] = useState("todos");

  const recarregar = useCallback(async () => {
    if (!nucleo) return;
    setCarregando(true);
    try {
      const membros = await carregarMembrosGestao(nucleo);
      setAtletas(membros.filter((m) => (m.perfil || "").toLowerCase().includes("atleta")));
    } catch (e) {
      setStatus({ tipo: "erro", texto: `Não consegui carregar: ${e.message}` });
    } finally {
      setCarregando(false);
    }
  }, [nucleo]);

  useEffect(() => { recarregar(); }, [recarregar]);

  // As fotos vêm depois da lista, e só as que existem.
  //
  // `temFotoCadastro` chega junto do cadastro justamente para isto: sem ele
  // seriam 34 chamadas, a maioria para descobrir que não há foto. A imagem
  // não vem por link porque é a imagem de um menor, e link sobrevive à
  // sessão; o custo disso é uma chamada por atleta com foto.
  useEffect(() => {
    let vivo = true;
    const comFoto = atletas.filter((a) => a.temFotoCadastro && !fotos[a.uid]);
    if (!comFoto.length) return undefined;

    Promise.all(comFoto.map((a) =>
      obterFotoDeCadastro(a.uid)
        .then((r) => [a.uid, `data:${r.tipo};base64,${r.base64}`])
        .catch(() => null)
    )).then((pares) => {
      if (!vivo) return;
      const novas = {};
      pares.forEach((p) => { if (p) novas[p[0]] = p[1]; });
      if (Object.keys(novas).length) setFotos((f) => ({ ...f, ...novas }));
    });

    return () => { vivo = false; };
    // `fotos` fica fora das dependências de propósito: ele é o que o efeito
    // escreve, e incluí-lo faria o efeito rodar de novo a cada foto que
    // chega, buscando as restantes outra vez.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [atletas]);

  const modalidades = useMemo(
    () => [...new Set(atletas.map((a) => a.modalidade).filter(Boolean))].sort(),
    [atletas],
  );

  const lista = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    return atletas
      .filter((a) => !modalidade || a.modalidade === modalidade)
      .filter((a) => situacao === "todos"
        || (situacao === "ativos" ? a.ativo : !a.ativo))
      .filter((a) => !termo || (a.nome || "").toLowerCase().includes(termo))
      .sort((a, b) => {
        if (a.ativo !== b.ativo) return a.ativo ? -1 : 1;
        return String(a.nome || "").localeCompare(String(b.nome || ""), "pt-BR");
      });
  }, [atletas, busca, modalidade, situacao]);

  // Agrupado por modalidade, como no documento da coordenação: é assim que
  // a lista é conferida, uma modalidade por vez.
  const grupos = useMemo(() => {
    const mapa = new Map();
    lista.forEach((a) => {
      const chave = a.modalidade || "Sem modalidade";
      if (!mapa.has(chave)) mapa.set(chave, []);
      mapa.get(chave).push(a);
    });
    return [...mapa.entries()].sort((a, b) => a[0].localeCompare(b[0], "pt-BR"));
  }, [lista]);

  async function enviarFoto(atleta, file) {
    if (!file) return;
    setOcupado(atleta.uid);
    setStatus(null);
    try {
      const dataUrl = await lerArquivoComoDataUrl(file);
      const reduzida = await reduzirImagem(dataUrl);
      const { base64, tipo } = partesDoDataUrl(reduzida);
      await salvarFotoDeCadastro({ alunoUid: atleta.uid, fotoBase64: base64, tipoArquivo: tipo });
      setFotos((f) => ({ ...f, [atleta.uid]: reduzida }));
      setAtletas((lst) => lst.map((a) => (a.uid === atleta.uid ? { ...a, temFotoCadastro: true } : a)));
      setStatus({ tipo: "ok", texto: `Foto de ${atleta.nome} atualizada.` });
    } catch (e) {
      setStatus({ tipo: "erro", texto: e.message || "Não foi possível enviar a foto." });
    } finally {
      setOcupado(null);
    }
  }

  async function removerFoto(atleta) {
    if (!window.confirm(`Remover a foto de cadastro de ${atleta.nome}?`)) return;
    setOcupado(atleta.uid);
    setStatus(null);
    try {
      await removerFotoDeCadastro(atleta.uid);
      setFotos((f) => { const n = { ...f }; delete n[atleta.uid]; return n; });
      setAtletas((lst) => lst.map((a) => (a.uid === atleta.uid ? { ...a, temFotoCadastro: false } : a)));
      setStatus({ tipo: "ok", texto: "Foto removida." });
    } catch (e) {
      setStatus({ tipo: "erro", texto: e.message || "Não foi possível remover." });
    } finally {
      setOcupado(null);
    }
  }

  if (!nucleo) return <Vazio titulo="Selecione um núcleo." />;
  if (carregando) return <Carregando linhas={6} />;

  const ativos = atletas.filter((a) => a.ativo).length;
  const comFoto = atletas.filter((a) => a.temFotoCadastro).length;
  const semCpf = atletas.filter((a) => !a.cpf).length;
  const semGenero = atletas.filter((a) => !a.sexo).length;

  return (
    <div style={{ display: "grid", gap: ESP.lg }}>
      {status && <StatusBox tipo={status.tipo} msg={status.texto} />}

      <Card
        titulo={`Atletas do núcleo ${nomeDoNucleo(nucleo)}`}
        sub="Os dados vêm do cadastro, em Gerenciar usuários. Aqui se confere o conjunto e se anexa o retrato de uniforme."
      >
        <FileiraDeEstatisticas minimo={150}>
          <Estatistica
            rotulo="Atletas" valor={atletas.length}
            contexto={`${plural(ativos, "ativo")} · ${plural(atletas.length - ativos, "desligado")}`}
          />
          <Estatistica
            rotulo="Com foto" valor={comFoto}
            contexto={comFoto < atletas.length ? `faltam ${atletas.length - comFoto}` : "todos"}
          />
          <Estatistica
            rotulo="Sem CPF" valor={semCpf}
            contexto={semCpf ? "preencher em Gerenciar usuários" : "nenhum"}
          />
          <Estatistica
            rotulo="Sem gênero" valor={semGenero}
            contexto={semGenero ? "trava a avaliação física" : "nenhum"}
          />
        </FileiraDeEstatisticas>

        <div style={{ display: "flex", gap: ESP.md, flexWrap: "wrap", marginTop: ESP.lg }}>
          <CampoDeBusca valor={busca} onChange={setBusca} placeholder="Buscar atleta" />
          <Sel
            value={modalidade} onChange={setModalidade}
            options={[{ value: "", label: "Todas as modalidades" },
              ...modalidades.map((m) => ({ value: m, label: m }))]}
          />
          <Sel
            value={situacao} onChange={setSituacao}
            options={[
              { value: "todos", label: "Ativos e desligados" },
              { value: "ativos", label: "Só os ativos" },
              { value: "desligados", label: "Só os desligados" },
            ]}
          />
        </div>
      </Card>

      {lista.length === 0 ? (
        <Card><Vazio titulo="Nenhum atleta neste recorte." /></Card>
      ) : (
        grupos.map(([nomeDaModalidade, doGrupo]) => (
          <Card key={nomeDaModalidade} titulo={nomeDaModalidade} sub={`${doGrupo.length} atleta${doGrupo.length > 1 ? "s" : ""}`}>
            <div style={{ display: "grid", gap: ESP.md }}>
              {doGrupo.map((a) => (
                <CartaoDoAtleta
                  key={a.uid}
                  atleta={a}
                  foto={fotos[a.uid]}
                  ocupado={ocupado === a.uid}
                  isMobile={isMobile}
                  onEnviar={(file) => enviarFoto(a, file)}
                  onRemover={() => removerFoto(a)}
                />
              ))}
            </div>
          </Card>
        ))
      )}
    </div>
  );
}

// ── O cartão de um atleta ───────────────────────────────────

function CartaoDoAtleta({ atleta, foto, ocupado, isMobile, onEnviar, onRemover }) {
  const entrada = useRef(null);
  const anos = idadeEm(atleta.dataNascimento);
  const lado = isMobile ? 92 : 116;

  // O que ainda falta preencher, dito por extenso. A contagem lá em cima diz
  // quantos faltam; aqui diz qual campo, que é o que leva alguém a abrir o
  // cadastro e preencher.
  const faltando = [
    !atleta.sexo && "gênero",
    !atleta.cpf && "CPF",
    !atleta.dataNascimento && "nascimento",
  ].filter(Boolean);

  return (
    <div style={{
      display: "flex", gap: ESP.lg, alignItems: "flex-start", flexWrap: "wrap",
      border: `1px solid ${BORDA}`, borderRadius: RAIO.md, padding: ESP.md,
      background: atleta.ativo ? BRANCO : FUNDO_SUAVE,
    }}>
      {/* O retrato do desligado fica em preto e branco. É a marca que se lê
          antes de qualquer texto, e diz "esteve no programa" sem ocupar uma
          linha para dizer isso. */}
      {foto ? (
        <img
          src={foto} alt={`Foto de ${atleta.nome}`}
          style={{
            width: lado, height: Math.round(lado * 1.25), objectFit: "cover",
            borderRadius: RAIO.sm, border: `1px solid ${BORDA}`, flexShrink: 0,
            filter: atleta.ativo ? "none" : "grayscale(1)",
          }}
        />
      ) : (
        <div style={{
          width: lado, height: Math.round(lado * 1.25), borderRadius: RAIO.sm,
          border: `1px dashed ${BORDA}`, background: FUNDO_SUAVE, flexShrink: 0,
          display: "flex", flexDirection: "column", alignItems: "center",
          justifyContent: "center", gap: 6,
          filter: atleta.ativo ? "none" : "grayscale(1)",
        }}>
          <Avatar nome={atleta.nome} tamanho={44} />
          <span style={{ fontSize: 11, color: CINZA_ESC }}>sem foto</span>
        </div>
      )}

      <div style={{ flex: 1, minWidth: 220 }}>
        <Linha rotulo="Nome" valor={atleta.nome} destaque />
        <Linha rotulo="Gênero" valor={ROTULO_DO_GENERO[atleta.sexo] || ""} />
        <Linha rotulo="CPF" valor={atleta.cpf} />
        <Linha
          rotulo="Data de Nascimento"
          valor={atleta.dataNascimento ? `${emBR(atleta.dataNascimento)}${anos !== null ? ` (${anos} anos)` : ""}` : ""}
        />
        <Linha
          rotulo="Categoria"
          valor={[atleta.modalidade, atleta.categoria].filter(Boolean).join(" · ")}
        />
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: ESP.sm, alignItems: "flex-start", minWidth: 150 }}>
        <Etiqueta tom={atleta.ativo ? "ok" : "neutro"} ponto>
          {atleta.ativo ? "Ativo" : "Desligado"}
        </Etiqueta>

        {faltando.length > 0 && (
          <span style={{ fontSize: 11.5, color: PERIGO, fontWeight: 600, lineHeight: 1.5 }}>
            falta {faltando.join(", ")}
          </span>
        )}

        <input
          ref={entrada} type="file" accept="image/jpeg,image/png"
          style={{ display: "none" }}
          onChange={(e) => {
            const file = e.target.files && e.target.files[0];
            e.target.value = "";    // deixa reenviar o mesmo arquivo depois de um erro
            onEnviar(file);
          }}
        />
        <div style={{ display: "flex", gap: ESP.sm, flexWrap: "wrap" }}>
          <Btn variant="discreto" tamanho="sm" disabled={ocupado}
            onClick={() => entrada.current && entrada.current.click()}>
            {ocupado ? "Enviando…" : foto ? "Trocar foto" : "Enviar foto"}
          </Btn>
          {foto && (
            <Btn variant="discreto" tamanho="sm" disabled={ocupado} onClick={onRemover}>
              Remover
            </Btn>
          )}
        </div>
      </div>
    </div>
  );
}

function Linha({ rotulo, valor, destaque = false }) {
  return (
    <div style={{ display: "flex", gap: 7, alignItems: "baseline", padding: "2px 0", flexWrap: "wrap" }}>
      <span style={{ ...TIPO.apoio, color: CINZA_ESC, fontWeight: 700 }}>{rotulo}:</span>
      <span style={{
        ...TIPO.corpo, color: valor ? TEXTO : CINZA_ESC,
        fontWeight: destaque ? 700 : 400,
      }}>
        {valor || "—"}
      </span>
    </div>
  );
}
